import { useState, useCallback } from 'react';
import { useLoader } from '../contexts/LoaderContext';
import { useAlert } from '../contexts/AlertContext';
import { TableResource } from '../Pages/Resources/types';
import noImage from '../Assets/Images/no-img.jpg';
import { resources } from '../api/resources';

interface ResourcesResponse {
  resources: TableResource[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface UseResourceApiParams {
  agencyFilter: string;
  typeFilter: string;
  categoryFilter: string;
  currentPage: number;
  itemsPerPage: number;
}

export const useResourceApi = ({
  agencyFilter,
  typeFilter,
  categoryFilter,
  currentPage,
  itemsPerPage,
}: UseResourceApiParams) => {
  const { showLoader, hideLoader, setLoaderProgress } = useLoader();
  const { showAlert } = useAlert();

  const [resourcesData, setResourcesData] = useState<ResourcesResponse>({
    resources: [],
    pageNumber: 1,
    pageSize: itemsPerPage,
    totalCount: 0,
    totalPages: 0,
  });

  const fetchResources = useCallback(async () => {
    showLoader();
    try {
      const params = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        agencyId: agencyFilter ? parseInt(agencyFilter) : undefined,
        categoryId: categoryFilter ? parseInt(categoryFilter) : undefined,
        type: typeFilter || undefined,
      };

      const response = await resources.getList(params);
      setResourcesData({
        ...response,
        resources: response.resources.map((res) => ({
          ...res,
          agency: res.agency,
          thumbnail:
            res.thumbnail && /\.(jpeg|jpg|gif|png)$/i.test(res.thumbnail) ? res.thumbnail : noImage,
        })),
      });
      hideLoader();
    } catch (error) {
      showAlert('Failed to load resources', 'error');
      hideLoader();
    }
  }, [
    agencyFilter,
    typeFilter,
    categoryFilter,
    currentPage,
    itemsPerPage,
    showAlert,
    showLoader,
    hideLoader,
  ]);

  const downloadResource = useCallback(
    async (resourceId: number) => {
      let fallbackTimer: number | null = null;
      let fallbackProgress = 0;
      let progressWasReported = false;

      const stopFallback = () => {
        if (!fallbackTimer) return;
        clearInterval(fallbackTimer);
        fallbackTimer = null;
      };

      try {
        showLoader();
        setLoaderProgress(0);
        fallbackTimer = window.setInterval(() => {
          if (progressWasReported) {
            stopFallback();
            return;
          }
          fallbackProgress = Math.min(fallbackProgress + 5, 90);
          setLoaderProgress(fallbackProgress);
        }, 400);

        const { blob, filename } = await resources.download(resourceId, (value) => {
          if (value > 0) {
            progressWasReported = true;
            stopFallback();
            setLoaderProgress(value);
            return;
          }

          setLoaderProgress(0);
        });
        setLoaderProgress(100);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showAlert('Resource downloaded successfully', 'success');
      } catch (error) {
        showAlert('Failed to download resource', 'error');
      } finally {
        stopFallback();
        hideLoader();
      }
    },
    [showLoader, hideLoader, showAlert, setLoaderProgress]
  );

  const deleteResource = useCallback(
    async (resourceId: number) => {
      try {
        showLoader();
        await resources.delete(resourceId);
        await fetchResources();
        showAlert('Resource deleted successfully', 'success');
      } catch (error) {
        showAlert('Failed to delete resource', 'error');
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, showAlert, fetchResources]
  );

  const deleteMultipleResources = useCallback(
    async (resourceIds: number[]) => {
      try {
        showLoader();
        await resources.deleteMultiple(resourceIds);
        await fetchResources();
        showAlert('Resources deleted successfully', 'success');
      } catch (error) {
        showAlert('Failed to delete resources', 'error');
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, showAlert, fetchResources]
  );

  return {
    resourcesData,
    fetchResources,
    downloadResource,
    deleteResource,
    deleteMultipleResources,
  };
};
